import React, { useState, useContext, useEffect } from "react";

import {
  DataGrid,
  GridColDef,
  jaJP,
} from "@mui/x-data-grid";

import moment from "moment";

import { ReloadContext } from "../../context/ReloadContext";
import { postSearchKikiKataban } from "../../api/kikiKataban";
import { renderCellExpand } from "../Common/GridCellExpand";
import KikiKatabanEditDialog from './KikiKatabanEditDialog'
import ToggleVisibilityDialog from '../Common/ToggleVisibilityDialog'

import {
  KikiKatabanData, KikiKatabanDataSet, KikiKatabanSearchParam
} from "../../types/WebData";

/**
 * DataGrid用にデータを編集
 *
 * @param {KikiKatabanDataSet[]} kikiKatabanDatas
 * @return {*} 
 */
const makeGridData = (kikiKatabanDatas: KikiKatabanDataSet[]) => {
  return kikiKatabanDatas.map((kikiKataban) => {
    // 日付フォーマット
    const updateDate = moment(kikiKataban.update_dt);
    return {
      ...kikiKataban,
      update_dt: updateDate.format("YYYY/MM/DD"),
    };
  });
};

/**
 * 全件データを１ページ分の行にsliceしている
 * 値に単位をつけるなどのデータの編集も、ここで１ページ分のみ行う
 */
const loadServerRows = (
  page: number,
  pageSize: number,
  allRows: KikiKatabanDataSet[]
): Promise<KikiKatabanDataSet[]> =>
  new Promise<KikiKatabanDataSet[]>((resolve) => {
    if (!allRows) return;
    resolve(
      // １ページ分にsliceした後に、データを編集するfunctionに渡している
      makeGridData(allRows.slice(page * pageSize, (page + 1) * pageSize))
    );
  });

const useQuery = (page: number, pageSize: number, allRows: any[]) => {
  const [rowCount, setRowCount] = React.useState<number | undefined>(undefined);
  const [data, setData] = React.useState<KikiKatabanDataSet[]>([]);

  useEffect(() => {
    let active = true;

    setRowCount(undefined);
    loadServerRows(page, pageSize, allRows).then((newRows) => {
      if (!active) {
        return;
      }
      setData(newRows);
      setRowCount(allRows.length);
    });

    return () => {
      active = false;
    };
  }, [page, pageSize, allRows]);

  return { data, rowCount };
};

interface RowsState {
  page: number;
  pageSize: number;
}

type Props = {
  searchParam: KikiKatabanSearchParam | null;
  setload: React.Dispatch<React.SetStateAction<boolean>>
};

/**
 * 機器型番一覧コンポーネント
 *
 * @return {*} 
 */
const KikiKatabanGrid = ({ searchParam, setload }: Props) => {
  const reloadContext = useContext(ReloadContext);
  const [kikiKatabanData, setKikiKatabanData] = useState<KikiKatabanData>({ data: [] });

  useEffect(() => {
    const getSearchData = async (param: KikiKatabanSearchParam) => {
      const result = await postSearchKikiKataban(param);
      setKikiKatabanData(result);
      if (result?.isError) {
        await reloadContext?.setSnackbarInfo({
          isOpen: true,
          type: "error",
          message: result.message,
        });
      }
      setload(false);
    };
    if (!searchParam) {
      getSearchData({ kiki_shubetsu_code: 0, seihin_mei: "", kataban: "", is_visible: false })
    } else {
      getSearchData(searchParam);
    }
  }, [searchParam, reloadContext?.reload]);

  const columns: GridColDef[] =
    [
      {
        field: "id",
        headerName: "ID",
        hide: true,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: "kiki_shubetsu_mei",
        headerName: "機器種別",
        width: 150,
        sortable: false,
        disableColumnMenu: true,
        renderCell: renderCellExpand,
      },
      {
        field: "seihin_mei",
        headerName: "製品名",
        width: 200,
        sortable: false,
        disableColumnMenu: true,
        renderCell: renderCellExpand,
      },
      {
        field: "kataban",
        headerName: "機器型番",
        width: 200,
        sortable: false,
        disableColumnMenu: true,
        renderCell: renderCellExpand,
      },
      {
        field: "kiki_himozukesaki_mei",
        headerName: "機器紐づけ先",
        width: 120,
        sortable: false,
        disableColumnMenu: true,
        renderCell: renderCellExpand,
      },
      {
        field: "update_dt",
        headerName: "更新日",
        width: 100,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: 'editButton',
        headerName: "",
        width: 20,
        align: "center",
        sortable: false,
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) => (
          <KikiKatabanEditDialog
            kikiKatabanId={params.row.id} />
        )
      },
      {
        field: "toggleVisibilityDialog",
        headerName: "",
        width: 20,
        align: "center",
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) => (
          <ToggleVisibilityDialog deleteId={params.row.id} isVisible={!params.row.del_flg} tableName="kikiKataban" />
        ),
      },
    ];

  // ページのstate　１ページに１０行
  const [rowsState, setRowsState] = React.useState<RowsState>({
    page: 0,
    pageSize: 10,
  });

  const { data, rowCount } = useQuery(
    rowsState.page,
    rowsState.pageSize,
    kikiKatabanData.data
  );

  // データの読み込みが間に合ってなくてrowCountがundefinedのままの場合の対策
  const [rowCountState, setRowCountState] = React.useState(rowCount || 0);
  React.useEffect(() => {
    setRowCountState((prevRowCountState) =>
      rowCount !== undefined ? rowCount : prevRowCountState
    );
  }, [rowCount, setRowCountState]);

  return (
    <div style={{ height: 650, width: "100%" }}>
      <DataGrid
        rows={data}
        columns={columns}
        rowCount={rowCountState}
        {...rowsState}
        rowsPerPageOptions={[10]}
        pagination
        paginationMode="server"
        sortModel={[{ field: "id", sort: "asc" }]}
        onPageChange={(page) => setRowsState((prev) => ({ ...prev, page }))}
        onPageSizeChange={(pageSize) =>
          setRowsState((prev) => ({ ...prev, pageSize }))
        }
        localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
      />
    </div>
  );
};

export default KikiKatabanGrid;